import logProPurchaseBuyNowClicked from '@alltrails/analytics/events/logProPurchaseBuyNowClicked';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import Button from '@alltrails/shared/denali/components/Button';
import Link from '@alltrails/shared/denali/components/Link';
import Typography from '@alltrails/shared/denali/components/Typography';
import { useState, ReactNode, ComponentProps, useRef } from 'react';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import ProResponse from 'types/ProResponse';
import { getPurchaseScreenTypePlan } from 'utils/amplitudeHelper';
import { execute } from 'utils/recaptcha_helpers';
import { validate } from 'components/forms/CreditCardForm/CreditCardForm';
import classNames from 'classnames';
import CountryCode from 'types/CountryCode';
import { ApiError } from '@alltrails/shared/api';
import useTokenizedFormValidation from 'components/forms/TokenizedCreditCardForm/hooks/useTokenizedFormValidation';
import useFeatures from 'hooks/useFeatures';
import logError from 'utils/logError';
import { Recurly, RecurlyError, TokenPayload } from '@recurly/recurly-js';
import * as styles from './Checkout.module.scss';
import CouponCode from './CouponCode';
import PaymentForm from './PaymentForm';

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1

type Props = {
  billedHeading?: ReactNode;
  billedSubheading?: ReactNode;
  billedSubheadingRight?: ReactNode;
  billedToday: ReactNode;
  billingCountry: CountryCode;
  buttonText: ReactNode;
  className?: string;
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  finePrint?: ReactNode;
  heading: ReactNode;
  headingClassName?: string;
  onApplePayToken: ComponentProps<typeof PaymentForm>['onApplePayToken'];
  onGooglePayToken: ComponentProps<typeof PaymentForm>['onGooglePayToken'];
  onPayPalToken: ComponentProps<typeof PaymentForm>['onPayPalToken'];
  plan: Plan;
  setCoupon?: (arg0: Coupon) => void;
  setPlanToAnnual?: () => void;
  subheading?: ReactNode;
  multiyearConversion?: boolean;
  webProPurchaseLocation: WebProPurchaseLocation;
  features: ReactNode;
  onBillingCountryChange: (countryCode: string, response: any) => void;
  onSubmit: ({
    billing_first_name,
    billing_last_name,
    cc_csc,
    cc_expiration_month,
    cc_expiration_year,
    cc_number,
    recaptcha,
    zip
  }: {
    billing_first_name: string;
    billing_last_name: string;
    cc_csc: string;
    cc_expiration_month: string;
    cc_expiration_year: string;
    cc_number: string;
    recaptcha: string;
    zip: string;
  }) => void;
  onSubmitTokenizedCardForm?: (payload: { cc_token: string; recaptcha: string }) => void;
  reactRecurlyInstance?: Recurly;
  shouldUseTokenizedCC?: boolean;
};

function CheckoutBase({
  buttonText,
  billedHeading,
  billedToday,
  billedSubheading,
  billingCountry,
  className,
  coupon,
  currencyCode,
  finePrint,
  heading,
  headingClassName,
  onApplePayToken,
  onGooglePayToken,
  onPayPalToken,
  plan,
  subheading,
  billedSubheadingRight,
  setCoupon,
  setPlanToAnnual,
  multiyearConversion,
  webProPurchaseLocation,
  features,
  onBillingCountryChange,
  onSubmit,
  onSubmitTokenizedCardForm,
  reactRecurlyInstance,
  shouldUseTokenizedCC
}: Props) {
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState<ProResponse['errors']>(); // TODO move up

  const [expiration, setExpiration] = useState('');
  const [number, setNumber] = useState('');
  const [cvv, setCvv] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [numberError, setNumberError] = useState<ReactNode>();
  const [cvvError, setCvvError] = useState('');
  const [postalCodeError, setPostalCodeError] = useState();
  const [expirationError, setExpirationError] = useState<ReactNode>();

  const ccTokenizationEnabled = useFeatures().includes('credit_card_tokenization');

  const formRef = useRef();

  const { validationErrors, setClientValidationErrors, setTokenRequestErrors, hasValidationErrors } = useTokenizedFormValidation();

  async function handleSubmit(e: React.KeyboardEvent | React.FormEvent) {
    e.preventDefault();

    if (submitting) {
      return;
    }

    if (coupon?.type !== 'GiftCard') {
      if (
        validate({
          name,
          cvv,
          number,
          expiration,
          postalCode,
          setCvvError,
          setNameError,
          setPostalCodeError,
          setExpirationError,
          setNumberError
        })
      ) {
        return;
      }
    }

    const fullName = name.split(' ');
    const fullNameClean = fullName.filter((item: string) => item !== '');
    const billing_first_name = fullNameClean[0];
    const billing_last_name = fullNameClean[fullNameClean.length - 1];

    const [cc_expiration_month, cc_expiration_year] = expiration.split(' / ');

    try {
      setSubmitting(true);

      const recaptcha = await execute('userPaymentAction');

      const body = {
        billing_country: billingCountry,
        recaptcha,
        cc_number: number,
        cc_expiration_month,
        cc_expiration_year,
        cc_csc: cvv,
        zip: postalCode,
        billing_first_name,
        billing_last_name
      };

      await onSubmit(body);
    } catch (e) {
      if (e instanceof ApiError) {
        setErrors(e.data);
      } else {
        setErrors(e);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const handleTokenizedCardSubmit = async (e: React.KeyboardEvent | React.FormEvent) => {
    e.preventDefault();

    if (submitting || hasValidationErrors()) {
      return;
    }

    const onRecurlyToken = async (err: RecurlyError, token: TokenPayload, recaptcha: string) => {
      if (err) {
        setTokenRequestErrors(err);
        setSubmitting(false);
      } else {
        const body = {
          recaptcha,
          cc_token: token.id
        };

        try {
          await onSubmitTokenizedCardForm(body);
        } catch (serverError) {
          if (serverError instanceof ApiError) {
            setErrors(serverError.data);
          } else {
            setErrors(serverError);
          }
        } finally {
          setSubmitting(false);
        }
      }
    };

    let recaptcha: string | null = null;

    try {
      setSubmitting(true);
      recaptcha = await execute('userPaymentAction');

      if (recaptcha) {
        reactRecurlyInstance.token(formRef.current, (err, token) => onRecurlyToken(err, token, recaptcha));
      }
    } catch (e) {
      if (e instanceof ApiError) {
        setErrors(e.data);
      } else {
        setErrors(e);
      }
      setSubmitting(false);
    }
  };

  return (
    <form
      ref={formRef}
      className={classNames(styles.content, className)}
      onSubmit={(e: React.KeyboardEvent | React.FormEvent) => {
        if (ccTokenizationEnabled && shouldUseTokenizedCC) {
          handleTokenizedCardSubmit(e);
        } else {
          handleSubmit(e);
        }
      }}
    >
      <div className={styles.tryArea}>
        <div className={classNames(styles.heading, headingClassName)}>{heading}</div>
        {subheading && (
          <div className={styles.subheading}>
            <Typography variant="text100">{subheading}</Typography>
          </div>
        )}
        {features}
      </div>
      <div className={styles.billedCopyright}>
        <div className={styles.billedArea}>
          <div className={styles.billed}>
            <div>{billedHeading || <FormattedMessage defaultMessage="Billed today" />}</div>
            <div>{billedToday}</div>
          </div>
          <div className={styles.hr} />
          {billedSubheading && (
            <div className={styles.after}>
              <div>{billedSubheading}</div>
              <div>{billedSubheadingRight}</div>
            </div>
          )}
          {setCoupon && (
            <div>
              <CouponCode coupon={coupon} setCoupon={setCoupon} setPlanToAnnual={setPlanToAnnual} />
            </div>
          )}
        </div>
        <div className={styles.copyright}>
          <FormattedMessage defaultMessage="{year} AllTrails, LLC All Rights Reserved" values={{ year: new Date().getFullYear() }} />
          <br />
          <Link className={styles.link} href="/privacy" target="_blank" noUnderline>
            <FormattedMessage defaultMessage="Privacy Policy" />
          </Link>{' '}
          |{' '}
          <Link className={styles.link} href="/terms" target="_blank" noUnderline>
            <FormattedMessage defaultMessage="Terms of Service" />
          </Link>
        </div>
      </div>
      <div className={styles.form}>
        <PaymentForm
          billingCountry={billingCountry}
          coupon={coupon}
          currencyCode={currencyCode}
          cvv={cvv}
          cvvError={cvvError}
          errors={errors}
          expiration={expiration}
          expirationError={expirationError}
          multiyearConversion={multiyearConversion}
          name={name}
          nameError={nameError}
          number={number}
          numberError={numberError}
          onBillingCountryChange={onBillingCountryChange}
          onApplePayToken={onApplePayToken}
          onGooglePayToken={onGooglePayToken}
          onPayPalToken={onPayPalToken}
          plan={plan}
          postalCode={postalCode}
          postalCodeError={postalCodeError}
          setClientValidationErrors={setClientValidationErrors}
          setCvv={setCvv}
          setCvvError={setCvvError}
          setErrors={setErrors}
          setExpiration={setExpiration}
          setExpirationError={setExpirationError}
          setName={setName}
          setNameError={setNameError}
          setNumber={setNumber}
          setNumberError={setNumberError}
          setPostalCode={setPostalCode}
          setPostalCodeError={setPostalCodeError}
          shouldUseTokenizedCC={shouldUseTokenizedCC}
          validationErrors={validationErrors}
        />
      </div>
      <div className={styles.button}>
        <Button
          className={styles.button}
          loading={submitting}
          onClick={() => {
            logProPurchaseBuyNowClicked({
              purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon),
              web_purchase_location: webProPurchaseLocation
            });
          }}
          testId="submit"
          text={buttonText}
          type="submit"
          variant="primary"
        />
      </div>
      <div className={styles.fine}>{finePrint}</div>
    </form>
  );
}

export default CheckoutBase;
